import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Suspense } from 'react';
import App from './App';
// import reportWebVitals from './reportWebVitals';
import { SnackbarProvider } from 'notistack';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { coreInit, initOptions } from './locales/i18n';
import { Amplify } from "aws-amplify";
import * as serviceWorker from './serviceWorker';
import { logger } from './utils/logger';

// 서비스 워커 제거 상태를 위한 타입 확장
declare global {
  interface Window {
    __serviceWorkerRemovalInProgress?: boolean;
    __serviceWorkerRemovalStarted?: boolean;
  }
}

coreInit(initOptions);
const IdentityPoolId = process.env.REACT_APP_IDENTITY_POOLID;
const region = process.env.REACT_APP_REGION;
const appId = process.env.REACT_APP_PINPOINT_APPID;

// Amplify 설정 - Cognito Identity Pool 추가
Amplify.configure({
  Auth: {
    identityPoolId: IdentityPoolId,
    region: region,
    mandatorySignIn: false
  },
  Analytics: {
    AWSPinpoint: {
      appId: appId,
      region: region,
      mandatorySignIn: false
    }
  }
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false
    },
  },
})

const theme = createTheme();
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <Suspense>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <SnackbarProvider maxSnack={3} anchorOrigin={{vertical: 'top', horizontal: 'right',}}>
              <App />
            </SnackbarProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </BrowserRouter>
    </Suspense>
  </React.StrictMode>,
);

// 기존 서비스 워커 제거 및 캐시 정리 (서비스 워커 없는 방식으로 전환)
const removeServiceWorkersAndCleanCache = async () => {
  // 중복 실행 방지
  if (window.__serviceWorkerRemovalInProgress) {
    logger.log('⚠️ 서비스 워커 제거가 이미 진행 중입니다.');
    return;
  }
  
  window.__serviceWorkerRemovalInProgress = true;
  
  logger.log('🔄 기존 서비스 워커 제거 및 캐시 정리 시작');
  
  try {
    logger.log('🔄 기존 서비스 워커 제거 및 캐시 정리 시작...');
    
    // 1. 기존 서비스 워커 완전 제거
    if ('serviceWorker' in navigator) {
      logger.log('🗑️ 기존 서비스 워커 제거 시작...');
      const registrations = await navigator.serviceWorker.getRegistrations();
      logger.log(`📋 발견된 서비스 워커: ${registrations.length}개`);
      
      if (registrations.length > 0) {
        for (let registration of registrations) {
          try {
            logger.log(`🗑️ 서비스 워커 제거 중: ${registration.scope}`);
            const unregistered = await registration.unregister();
            logger.log(`✅ 서비스 워커 제거 완료: ${registration.scope} (${unregistered ? '성공' : '실패'})`);
          } catch (error) {
            logger.warn(`⚠️ 서비스 워커 제거 실패: ${registration.scope}`, error);
          }
        }
      } else {
        logger.log('✅ 제거할 서비스 워커가 없습니다');
      }
    }
    
    // 2. 모든 캐시 완전 정리
    if ('caches' in window) {
      try {
        logger.log('🗂️ 안전한 캐시 정리 시작...');
        const cacheNames = await caches.keys();
        logger.log(`📦 발견된 캐시: ${cacheNames.length}개`);
        
        if (cacheNames.length > 0) {
          // 모든 캐시 삭제 (서비스 워커 없는 방식으로 전환)
          logger.log(`🗑️ 모든 캐시 삭제: ${cacheNames.length}개`);
          
          const deletePromises = cacheNames.map(async (name) => {
            try {
              const deleted = await caches.delete(name);
              logger.log(`🗑️ 캐시 삭제 ${name}: ${deleted ? '성공' : '실패'}`);
              return deleted;
            } catch (error) {
              logger.warn(`⚠️ 캐시 삭제 실패 ${name}:`, error);
              return false;
            }
          });
          
          const results = await Promise.all(deletePromises);
          const successCount = results.filter(Boolean).length;
          logger.log(`✅ 모든 캐시 정리 완료: ${successCount}/${cacheNames.length}개 성공`);
        } else {
          logger.log('✅ 정리할 캐시가 없습니다');
        }
      } catch (error) {
        logger.error('❌ 캐시 정리 중 오류:', error);
      }
    }
    
    // 2. 서비스 워커 업데이트 (기존 serviceWorker.ts 활용)
    logger.log('🔄 서비스 워커 업데이트 준비 완료');
    
    // 3. LocalStorage에서 캐시 관련 데이터 정리
    try {
      if (window.localStorage) {
        const localStorageKeys = Object.keys(localStorage);
        const cacheRelatedKeys = localStorageKeys.filter(key => 
          key.includes('cache') || 
          key.includes('sw') || 
          key.includes('service-worker') || 
          key.includes('workbox') ||
          key.includes('version')
        );
        
        if (cacheRelatedKeys.length > 0) {
          logger.log(`💾 LocalStorage 캐시 관련 키 ${cacheRelatedKeys.length}개 정리 중...`);
          cacheRelatedKeys.forEach(key => {
            localStorage.removeItem(key);
            logger.log(`🗑️ LocalStorage 제거: ${key}`);
          });
        }
      }
    } catch (error) {
      logger.warn('⚠️ LocalStorage 정리 중 오류:', error);
    }
    
    logger.log('🎯 캐시 정리 및 서비스 워커 업데이트 완료!');
  } catch (error) {
    logger.error('❌ 캐시 정리 및 서비스 워커 업데이트 중 오류:', error);
  } finally {
    window.__serviceWorkerRemovalInProgress = false;
  }
};

// 앱 시작 시 한 번만 실행 (무한 반복 방지)
if (!window.__serviceWorkerRemovalStarted) {
  window.__serviceWorkerRemovalStarted = true;
  
  // 개발 환경에서는 서비스 워커 제거 건너뛰기
  if (process.env.NODE_ENV === 'development') {
    logger.log('🔧 개발 환경 - 서비스 워커 제거 건너뛰기');
  } else {
    // 프로덕션 환경에서만 서비스 워커 제거 실행
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', removeServiceWorkersAndCleanCache);
    } else {
      removeServiceWorkersAndCleanCache();
    }
  }
}

// 서비스 워커 완전 비활성화 (서비스 워커 없는 방식으로 전환)
logger.log('🚫 서비스 워커 완전 비활성화 - 서비스 워커 없는 방식으로 전환');
serviceWorker.unregister();



// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(logger.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
